import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Linkedin } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    enquiry_type: "",
    message: "",
  });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.enquiry_type || !form.message) {
      setStatus("error");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        enquiry_type: "",
        message: "",
      });
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36"
      style={{ background: "var(--bg-secondary)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section label */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-sm tracking-[0.2em] uppercase mb-4"
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            color: "var(--gold-dark)",
          }}
        >
          Get in Touch
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-3xl md:text-4xl lg:text-5xl tracking-tight mb-4"
          style={{
            fontFamily: "var(--font-serif)",
            fontWeight: 400,
            color: "var(--text-primary)",
          }}
        >
          Begin a Conversation
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-base mb-16 max-w-2xl"
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 300,
            color: "rgba(26, 26, 26, 0.7)",
            lineHeight: 1.8,
          }}
        >
          We welcome enquiries from qualified investors and development
          partners. All correspondence is treated with the utmost
          confidentiality.
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="lg:col-span-7 flex flex-col gap-6"
            data-testid="contact-form"
          >
            <input
              data-testid="contact-name"
              type="text"
              name="full_name"
              placeholder="Full Name"
              value={form.full_name}
              onChange={handleChange}
              className="form-input"
              required
            />
            <input
              data-testid="contact-email"
              type="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              className="form-input"
              required
            />
            <input
              data-testid="contact-phone"
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={form.phone}
              onChange={handleChange}
              className="form-input"
            />

            {/* Dropdown using Shadcn Select */}
            <Select
              value={form.enquiry_type}
              onValueChange={(val) =>
                setForm({ ...form, enquiry_type: val })
              }
            >
              <SelectTrigger
                data-testid="contact-enquiry-type"
                className="bg-transparent border-0 border-b border-[#1A1A1A]/20 rounded-none px-0 py-3 h-auto text-[0.9375rem] font-light shadow-none focus:ring-0 focus:border-[#B8952A] transition-colors"
                style={{ fontFamily: "var(--font-sans)" }}
              >
                <SelectValue placeholder="Nature of Enquiry" />
              </SelectTrigger>
              <SelectContent
                className="bg-[#FAF8F5] border border-[#E5DED5] rounded-none shadow-lg"
              >
                <SelectItem
                  value="Investment Opportunity"
                  className="text-sm cursor-pointer focus:bg-[#F2EDE6]"
                  style={{ fontFamily: "var(--font-sans)" }}
                >
                  Investment Opportunity
                </SelectItem>
                <SelectItem
                  value="Development Partnership"
                  className="text-sm cursor-pointer focus:bg-[#F2EDE6]"
                  style={{ fontFamily: "var(--font-sans)" }}
                >
                  Development Partnership
                </SelectItem>
                <SelectItem
                  value="General Enquiry"
                  className="text-sm cursor-pointer focus:bg-[#F2EDE6]"
                  style={{ fontFamily: "var(--font-sans)" }}
                >
                  General Enquiry
                </SelectItem>
              </SelectContent>
            </Select>

            <textarea
              data-testid="contact-message"
              name="message"
              placeholder="Message"
              value={form.message}
              onChange={handleChange}
              className="form-textarea"
              rows={4}
              required
            />

            <div className="flex items-center gap-4 mt-4">
              <button
                data-testid="contact-submit-btn"
                type="submit"
                className="btn-gold"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Submit Enquiry"}
              </button>

              {status === "success" && (
                <span
                  data-testid="contact-success-msg"
                  className="text-sm"
                  style={{
                    fontFamily: "var(--font-sans)",
                    color: "var(--gold-dark)",
                    fontWeight: 500,
                  }}
                >
                  Thank you. We will be in touch shortly.
                </span>
              )}
              {status === "error" && (
                <span
                  data-testid="contact-error-msg"
                  className="text-sm"
                  style={{
                    fontFamily: "var(--font-sans)",
                    color: "#a0522d",
                    fontWeight: 500,
                  }}
                >
                  Please complete all required fields.
                </span>
              )}
            </div>
          </motion.form>

          {/* Contact details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-5 flex flex-col gap-8 lg:pl-8"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <MapPin
                  size={18}
                  strokeWidth={1.2}
                  style={{ color: "var(--gold-dark)", marginTop: "3px", flexShrink: 0 }}
                />
                <span
                  className="text-sm leading-relaxed"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontWeight: 300,
                    color: "var(--text-primary)",
                  }}
                >
                  20 Lexington Drive, Bella Vista NSW 2153
                </span>
              </div>

              <div className="flex items-start gap-4">
                <Mail
                  size={18}
                  strokeWidth={1.2}
                  style={{ color: "var(--gold-dark)", marginTop: "3px", flexShrink: 0 }}
                />
                <a
                  href="mailto:info@valcap.com.au"
                  data-testid="contact-email-link"
                  className="text-sm"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontWeight: 300,
                    color: "var(--text-primary)",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.color = "var(--gold-dark)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.color = "var(--text-primary)")
                  }
                >
                  info@valcap.com.au
                </a>
              </div>

              <div className="flex items-start gap-4">
                <Phone
                  size={18}
                  strokeWidth={1.2}
                  style={{ color: "var(--gold-dark)", marginTop: "3px", flexShrink: 0 }}
                />
                <a
                  href="tel:+61402266884"
                  data-testid="contact-phone-link"
                  className="text-sm"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontWeight: 300,
                    color: "var(--text-primary)",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.color = "var(--gold-dark)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.color = "var(--text-primary)")
                  }
                >
                  +61 402 266 884
                </a>
              </div>
            </div>

            {/* Divider */}
            <div
              className="w-12 h-px"
              style={{ background: "var(--gold-dark)" }}
            />

            {/* LinkedIn */}
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="flex items-center gap-3 text-sm"
              style={{
                fontFamily: "var(--font-sans)",
                fontWeight: 400,
                color: "var(--text-muted)",
                textDecoration: "none",
                letterSpacing: "0.05em",
                transition: "color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.target.style.color = "var(--gold-dark)")
              }
              onMouseLeave={(e) =>
                (e.target.style.color = "var(--text-muted)")
              }
            >
              <Linkedin size={18} strokeWidth={1.2} />
              <span>LinkedIn</span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
